import React, { useEffect, useMemo, useLayoutEffect } from "react";
import { ReactComponent as CircleIcon } from "./../../images/svg/circle.svg";
import { ReactComponent as ArrowLeftIcon } from "./../../images/svg/arrowLeft.svg";
import { ReactComponent as LoaderIcon } from "./../../images/svg/loader.svg";
import { gettext, cropr } from "../../adapter";
import Information from "./InformationEdit";
import LoadError from "../LoadError";
import NProgress from "../../libraries/progress";
import Loader from "../Loader";
import each from "lodash/each";
import { parseSensor } from "../../utils/sensorUtils";
import { useDispatch, useSelector } from "react-redux";
import { fetchSensor, storeSensor } from "../../actions/sensorActions";
import { useParams, useLocation } from "react-router-dom";

function scrollToTop(duration) {
  // cancel if already on top
  if (document.scrollingElement.scrollTop === 0) return;

  const totalScrollDistance = document.scrollingElement.scrollTop;
  let scrollY = totalScrollDistance,
    oldTimestamp = null;

  function step(newTimestamp) {
    if (oldTimestamp !== null) {
      // if duration is 0 scrollY will be -Infinity
      scrollY -= (totalScrollDistance * (newTimestamp - oldTimestamp)) / duration;
      if (scrollY <= 0) return (document.scrollingElement.scrollTop = 0);
      document.scrollingElement.scrollTop = scrollY;
    }
    oldTimestamp = newTimestamp;
    window.requestAnimationFrame(step);
  }
  window.requestAnimationFrame(step);
}

const SensorEdit = ({ apiUrl }) => {
  const sensorState = useSelector((state) => {
    return state.sensor;
  });

  const sensorUpdates = useSelector((state) => {
    return state.sensorUpdates;
  });

  const {
    sensor,
    isSaving,
    loadError,
    isFetching,
    lastDataPoint,
    domain,
    saveError,
    defaultDomain,
  } = useMemo(() => {
    return {
      ...sensorState,
      ...parseSensor(sensorState.sensor, sensorState.advice, 60),
    };
  }, [sensorState]);

  useLayoutEffect(() => {
    if (saveError) {
      scrollToTop(300);
    }
  });

  // Update the model when data is changed
  if (sensorUpdates.updatedName !== null) {
    sensor.updateName(sensorUpdates.updatedName);
  }
  if (sensorUpdates.updatedLatitude !== null) {
    sensor.updateLatitude(sensorUpdates.updatedLatitude);
  }
  if (sensorUpdates.updatedLongitude !== null) {
    sensor.updateLongitude(sensorUpdates.updatedLongitude);
  }
  if (sensorUpdates.updatedSoilType !== null) {
    sensor.soilType = sensorUpdates.updatedSoilType.value;
    // Update the sensor scale with the ground capacity
    sensor.updateScales(sensorUpdates.updatedSoilType.capacity);
  }
  if (sensorUpdates.updatedDepthVisibility !== null) {
    each(sensorUpdates.updatedDepthVisibility, function (value, key) {
      sensor.updateDepthVisibility(key, value);
    });
    sensor.calculateSoilMoisture();
  }
  if (sensorUpdates.updatedWarningPercentage !== null) {
    sensor.updateWarningPercentage(sensorUpdates.updatedWarningPercentage);
  }
  if (sensorUpdates.updatedTurnedOff !== null) {
    sensor.updateTurnedOff(true);
  }
  if (sensorUpdates.updatedScale !== null) {
    each(sensorUpdates.updatedScale, function (value, depth) {
      sensor.updateScale(depth, value);
    });
  }
  if (sensorUpdates.updatedFieldCapacity !== null) {
    each(sensorUpdates.updatedFieldCapacity, function (value, depth) {
      var sensorDepth = sensor.depths[depth];
      var newFieldCapacity =
        sensorDepth.originalRefillPoint +
        (value * (sensorDepth.originalFieldCapacity - sensorDepth.originalRefillPoint)) /
          sensorDepth.originalScale;

      sensor.updateFieldCapacity(depth, value, newFieldCapacity);
    });
  }
  if (sensorUpdates.updatedRefillPoint !== null) {
    each(sensorUpdates.updatedRefillPoint, function (value, depth) {
      var sensorDepth = sensor.depths[depth];
      var newRefillPoint =
        sensorDepth.originalRefillPoint +
        (value * (sensorDepth.originalFieldCapacity - sensorDepth.originalRefillPoint)) /
          sensorDepth.originalScale;
      sensor.updateRefillPoint(depth, value, newRefillPoint);
    });
  }
  if (sensorUpdates.updatedShowTemperature !== null) {
    sensor.showTemperature = sensorUpdates.updatedShowTemperature;
  }
  if (sensorUpdates.updatedShowPrecipitation !== null) {
    sensor.showPrecipitation = sensorUpdates.updatedShowPrecipitation;
  }
  if (sensorUpdates.updatedStartDate !== null) {
    sensor.updateStartDate(sensorUpdates.updatedStartDate);
  }
  if (sensorUpdates.updatedEndDate !== null) {
    sensor.updateEndDate(sensorUpdates.updatedEndDate);
  }

  const { sensorId } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  // const history = useHistory();

  const onSaveChanges = () => {
    window.localStorage.removeItem("timestampCache" + cropr.farm.id);
    window.localStorage.removeItem("sensorsCache" + cropr.farm.id);
    if (sensor.id) {
      dispatch(storeSensor(sensor, apiUrl));
    } else {
      console.warn("No sensor ID set in body data attribute sensor-id.");
    }
  };

  useEffect(() => {
    NProgress.start();
    if (sensorId) {
      dispatch(fetchSensor(apiUrl, sensorId, null, true, "", false));
    }
  }, [sensorId, dispatch, apiUrl]);

  if (!sensor) {
    if (loadError) {
      NProgress.done();
      return <LoadError />;
    } else {
      return <Loader message={gettext("Loading sensor data...")} />;
    }
  }

  if (!isFetching) {
    NProgress.done();
  }

  var iconClass = "icon is-uknown";
  if (sensor.status === "new") {
    iconClass = "icon is-small text-gray";
  } else if (sensor.active === undefined && sensor.enabled === undefined) {
    iconClass = "icon is-small text-gray";
  } else if (sensor.active && sensor.enabled) {
    iconClass = "icon is-small text-green";
  } else if (!sensor.enabled && sensor.active) {
    iconClass = "icon is-small text-yellow";
  } else if (sensor.enabled && !sensor.active) {
    iconClass = "icon is-small text-yellow";
  } else if (!sensor.enabled && !sensor.active) {
    iconClass = "icon is-small text-red";
  }
  var preloaderClass = "object-preloader is-loaded";
  var buttonClass = "button is-right";

  var sensorUrl = "";

  if (sensor.status === "new") {
    sensorUrl = location.pathname.replace(/\/\d*\/edit/, "") + location.search;
  } else {
    sensorUrl = location.pathname.replace("/edit", "/detail") + location.search;
  }

  var message = "";

  if (sensor.status === "new") {
    message = (
      <ul className="messages">
        <li className="message--neutral">
          {gettext("This sensor is new and needs to be configured on this page.")}
        </li>
      </ul>
    );
  }

  if (saveError) {
    message = (
      <ul className="messages">
        <li className="message--alert">
          {gettext("Some settings contain errors. Please correct before saving.")}
        </li>
      </ul>
    );
  }

  var savingComponent = (
    <span>
      <LoaderIcon className="loader button-loader" /> {gettext("saving...")}
    </span>
  );

  if (isSaving) {
    preloaderClass = "object-preloader is-loading";
    buttonClass = "button is-right is-loading";
  }

  // Redirect if saving is completed
  if (isSaving === "done") {
    sensorUrl = location.pathname.replace("/edit", "/detail") + location.search;
    window.location.href = sensorUrl;
    // history.push(sensorUrl);
  }
  return (
    <div>
      <div className="l-single is-wide buttons--header">
        <h1>
          {sensor.name} <CircleIcon className={iconClass} />
        </h1>
        <div className="buttons is-inline">
          <a href={sensorUrl} className="button--normal">
            <ArrowLeftIcon />
            {gettext("Cancel")}
          </a>
          {/* eslint-disable-next-line */}
          <a className={buttonClass} onClick={onSaveChanges}>
            {isSaving ? savingComponent : gettext("Save changes")}
          </a>
        </div>
      </div>

      <div className="l-single is-wide">
        <div className="panel has-object-preloader has-spacing">
          <div className={preloaderClass}>
            <div className="object-preloader--content">
              <LoaderIcon className="loader" />
            </div>
          </div>
          {message}
          <Information
            sensor={sensor}
            lastDataPoint={lastDataPoint}
            domain={domain ? domain : defaultDomain}
          />
        </div>

        <div className="buttons is-inline">
          <a href={sensorUrl} className="button--normal">
            <ArrowLeftIcon />
            {gettext("Cancel")}
          </a>
          &nbsp;&nbsp;
          {/* eslint-disable-next-line */}
          <a className={buttonClass} onClick={onSaveChanges}>
            {isSaving ? savingComponent : gettext("Save changes")}
          </a>
        </div>
      </div>
    </div>
  );
};

export default SensorEdit;
